import React, { useState } from 'react';
import { Container, Navbar, Nav, NavDropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { SUPPORTED_LOCALES, LOCALE_LABELS } from '../../i18n/translations';
import './Header.css';

/** Üst şerit: mobilde her dilde mutfak-logo6; masaüstünde TR geniş logo / EN-FR tek dil dosyası */
function getNavbarLogoSources(locale) {
  const mobileSrc = '/images/mutfak-logo6.png';
  if (locale === 'en') {
    return { mobileSrc, desktopSrc: '/images/mutfak-logoen.png' };
  }
  if (locale === 'fr') {
    return { mobileSrc, desktopSrc: '/images/mutfak-logofr.png' };
  }
  return { mobileSrc, desktopSrc: '/images/mutfak-logo5.png' };
}

/** Mobil yan menü üst görseli (navbar ile aynı özet logo) */
function getSidebarLogoSrc() {
  return '/images/mutfak-logo6.png';
}

/** Sağ üst AKMB logosu (TR varsayılan PNG; EN / FR dil dosyaları cdtst içinde) */
function getAkmbLogoSrc(locale) {
  if (locale === 'en') return '/images/cdtst/akmbLogoEn.png';
  if (locale === 'fr') return '/images/cdtst/akmbLogoFr.png';
  return '/images/cdtst/akmbLogo.png';
}

function LanguageSwitcher({ className = '' }) {
  const { locale, setLocale, t } = useLanguage();
  return (
    <div className={`language-switcher ${className}`.trim()} role="group" aria-label={t('languageName')}>
      {SUPPORTED_LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          className={`language-switcher__btn ${locale === code ? 'is-active' : ''}`}
          onClick={() => setLocale(code)}
          title={LOCALE_LABELS[code]}
          aria-label={LOCALE_LABELS[code]}
          aria-pressed={locale === code}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

const Header = () => {
  const { t, locale } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAuthorsDropdownOpen, setIsAuthorsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState(null);

  const handleDropdownClick = (dropdownName) => {
    setExpandedDropdown(expandedDropdown === dropdownName ? null : dropdownName);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    document.body.style.overflow = !isSidebarOpen ? 'hidden' : 'auto';
  };

  const { mobileSrc, desktopSrc } = getNavbarLogoSources(locale);
  const akmbLogoSrc = getAkmbLogoSrc(locale);

  return (
    <>
      <Navbar bg="akm-red" expand="md" className="py-2">
        <Container>
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="logo-container">
              <Link to="/">
                <picture>
                  <source media="(max-width: 767px)" srcSet={mobileSrc} />
                  <img src={desktopSrc} alt={t('siteNameShort')} className="logo-img" />
                </picture>
              </Link>
            </div>
            <div className="akm-logo-container">
              <a href="https://www.akmb.gov.tr" target="_blank" rel="noopener noreferrer">
                <img src={akmbLogoSrc} alt="AKM Logo" className="akm-logo-img" />
              </a>
            </div>
          </div>
        </Container>
      </Navbar>

      <Navbar bg="akm-gray" expand="md" className="py-0 navbar-light">
        <Container className="d-flex flex-wrap align-items-center">
          <button
            type="button"
            className="navbar-toggler"
            onClick={toggleSidebar}
            aria-controls="navbar-nav"
            aria-expanded={isSidebarOpen}
            aria-label={t('meta.navToggle')}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="mobile-language d-flex d-md-none ms-auto align-items-center">
            <LanguageSwitcher className="language-switcher--mobile" />
          </div>

          <div className="desktop-menu flex-grow-1 w-100">
            <div className="nav-desktop-bar d-none d-md-flex align-items-center w-100 flex-wrap gap-2">
              <Nav className="nav-desktop-bar__links flex-grow-1 flex-shrink-1 flex-wrap align-items-center">
              <Link to="/" className="nav-link text-white px-3 py-2">
                {t('nav.home')}
              </Link>
              <Link to="/hakkinda" className="nav-link text-white px-3 py-2">
                {t('nav.about')}
              </Link>

              <NavDropdown
                title={
                  <>
                    {t('nav.entries')}
                    <i className="fas fa-chevron-down dropdown-icon"></i>
                  </>
                }
                id="maddeler-dropdown"
                className="custom-dropdown"
                show={isDropdownOpen}
                onMouseEnter={() => setIsDropdownOpen(true)}
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <Link to="/maddeler" className="dropdown-item">
                  {t('nav.entriesAll')}
                </Link>
                <Link to="/maddeler?group=A-E" className="dropdown-item dropdown-item-a-e">
                  A-E
                </Link>
                <Link to="/maddeler?group=F-J" className="dropdown-item dropdown-item-f-j">
                  F-J
                </Link>
                <Link to="/maddeler?group=K-P" className="dropdown-item dropdown-item-k-p">
                  K-P
                </Link>
                <Link to="/maddeler?group=R-Z" className="dropdown-item dropdown-item-r-z">
                  R-Z
                </Link>
                <Link to="/maddeler?group=0-9" className="dropdown-item dropdown-item-0-9">
                  0-9
                </Link>
              </NavDropdown>

              <NavDropdown
                title={
                  <>
                    {t('nav.authors')}
                    <i className="fas fa-chevron-down dropdown-icon"></i>
                  </>
                }
                id="yazarlar-dropdown"
                className="custom-dropdown"
                show={isAuthorsDropdownOpen}
                onMouseEnter={() => setIsAuthorsDropdownOpen(true)}
                onMouseLeave={() => setIsAuthorsDropdownOpen(false)}
              >
                <Link to="/yazarlar" className="dropdown-item">
                  {t('nav.authorsAll')}
                </Link>
                <Link to="/yazarlar?group=A-E" className="dropdown-item dropdown-item-a-e">
                  A-E
                </Link>
                <Link to="/yazarlar?group=F-J" className="dropdown-item dropdown-item-f-j">
                  F-J
                </Link>
                <Link to="/yazarlar?group=K-P" className="dropdown-item dropdown-item-k-p">
                  K-P
                </Link>
                <Link to="/yazarlar?group=R-Z" className="dropdown-item dropdown-item-r-z">
                  R-Z
                </Link>
                <Link to="/yazarlar?group=0-9" className="dropdown-item dropdown-item-0-9">
                  0-9
                </Link>
              </NavDropdown>

              <Link to="/gelismisArama" className="nav-link text-white px-3 py-2">
                {t('nav.advancedSearch')}
              </Link>
              <Link to="/harita" className="nav-link text-white px-3 py-2">
                {t('nav.map')}
              </Link>
              <Link to="/iletisim" className="nav-link text-white px-3 py-2">
                {t('nav.contact')}
              </Link>
              </Nav>
              <div className="nav-desktop-bar__lang flex-shrink-0">
                <LanguageSwitcher className="language-switcher--prominent" />
              </div>
            </div>
          </div>

          <div className={`mobile-sidebar ${isSidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
              <button type="button" className="close-button" onClick={toggleSidebar}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="sidebar-logo">
              <img src={getSidebarLogoSrc()} alt={t('siteNameShort')} className="sidebar-logo-img" />
            </div>
            <div className="sidebar-content">
              <Link to="/" className="sidebar-item" onClick={toggleSidebar}>
                {t('nav.home')}
              </Link>
              <Link to="/hakkinda" className="sidebar-item" onClick={toggleSidebar}>
                {t('nav.about')}
              </Link>

              <div className="sidebar-dropdown">
                <button
                  type="button"
                  className="sidebar-dropdown-header sidebar-dropdown-header--btn"
                  onClick={() => handleDropdownClick('articles')}
                >
                  {t('nav.entries')}
                  <i className={`fas fa-chevron-down ${expandedDropdown === 'articles' ? 'rotate' : ''}`}></i>
                </button>
                <div className={`sidebar-dropdown-content ${expandedDropdown === 'articles' ? 'show' : ''}`}>
                  <Link to="/maddeler" className="sidebar-item" onClick={toggleSidebar}>
                    {t('nav.entriesAll')}
                  </Link>
                  <Link to="/maddeler?group=A-E" className="sidebar-item" onClick={toggleSidebar}>
                    A-E
                  </Link>
                  <Link to="/maddeler?group=F-J" className="sidebar-item" onClick={toggleSidebar}>
                    F-J
                  </Link>
                  <Link to="/maddeler?group=K-P" className="sidebar-item" onClick={toggleSidebar}>
                    K-P
                  </Link>
                  <Link to="/maddeler?group=R-Z" className="sidebar-item" onClick={toggleSidebar}>
                    R-Z
                  </Link>
                  <Link to="/maddeler?group=0-9" className="sidebar-item" onClick={toggleSidebar}>
                    0-9
                  </Link>
                </div>
              </div>

              <div className="sidebar-dropdown">
                <button
                  type="button"
                  className="sidebar-dropdown-header sidebar-dropdown-header--btn"
                  onClick={() => handleDropdownClick('authors')}
                >
                  {t('nav.authors')}
                  <i className={`fas fa-chevron-down ${expandedDropdown === 'authors' ? 'rotate' : ''}`}></i>
                </button>
                <div className={`sidebar-dropdown-content ${expandedDropdown === 'authors' ? 'show' : ''}`}>
                  <Link to="/yazarlar" className="sidebar-item" onClick={toggleSidebar}>
                    {t('nav.authorsAll')}
                  </Link>
                  <Link to="/yazarlar?group=A-E" className="sidebar-item" onClick={toggleSidebar}>
                    A-E
                  </Link>
                  <Link to="/yazarlar?group=F-J" className="sidebar-item" onClick={toggleSidebar}>
                    F-J
                  </Link>
                  <Link to="/yazarlar?group=K-P" className="sidebar-item" onClick={toggleSidebar}>
                    K-P
                  </Link>
                  <Link to="/yazarlar?group=R-Z" className="sidebar-item" onClick={toggleSidebar}>
                    R-Z
                  </Link>
                  <Link to="/yazarlar?group=0-9" className="sidebar-item" onClick={toggleSidebar}>
                    0-9
                  </Link>
                </div>
              </div>

              <Link to="/gelismisArama" className="sidebar-item" onClick={toggleSidebar}>
                {t('nav.advancedSearch')}
              </Link>
              <Link to="/harita" className="sidebar-item" onClick={toggleSidebar}>
                {t('nav.map')}
              </Link>
              <Link to="/iletisim" className="sidebar-item" onClick={toggleSidebar}>
                {t('nav.contact')}
              </Link>
            </div>
          </div>

          {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}
        </Container>
      </Navbar>
    </>
  );
};

export default Header;
